import { addDays } from 'date-fns';
import { isEmpty, splitEvery, times } from 'ramda';

import { Maybe } from 'utils/types';

import * as helpers from './helpers';
import * as types from './types';

export class DaysGenerator {
  static NUMBER_OF_DAYS: number = 1000; // 999 | 365;
  static PERIOD_SIZE: number = 5;

  private days: types.TCalendarDay[] = [];

  constructor() {
    this.days = this.generateEmptyDays({ start: 1, end: DaysGenerator.NUMBER_OF_DAYS });
  }

  public get periods() {
    return this.splitByPeriods(this.days);
  }

  assignDates({ startDate }: { startDate: Maybe<types.DateString> }) {
    const getDate = (dayNumber: types.TCalendarDay['dayNumber']) => {
      if (!startDate) return null;

      const parsedDate = helpers.parseStringToDate(startDate);
      const incrementedDate = addDays(parsedDate, dayNumber - 1);
      return helpers.parseDateToString(incrementedDate);
    };

    this.days = this.days.map((day) => ({ ...day, date: getDate(day.dayNumber) }));
  }

  generateSlotsWitnEvents(calendarEvents: types.CalendarEvent[]) {
    if (isEmpty(calendarEvents)) return [];

    const initialStartDayNumber = calendarEvents[0].daysRange.start;
    const initialEndDayNumber = calendarEvents[0].daysRange.end || initialStartDayNumber;

    const maxEndDayNumber = calendarEvents.reduce((max, event) => {
      const endDayNumber = event.daysRange.end || event.daysRange.start;
      return endDayNumber > max ? endDayNumber : max;
    }, initialEndDayNumber);

    const emptyPeriodsDays = splitEvery(
      DaysGenerator.PERIOD_SIZE,
      this.generateEmptyDays({ start: this.days[0].dayNumber, end: maxEndDayNumber }),
    );

    const res = emptyPeriodsDays.map<types.DaySlots[]>((emptyPeriodDays) => {
      const { getPosition } = helpers.createPositionStorage();

      return emptyPeriodDays.map((emptyDay) => {
        const eventsPerDay = calendarEvents.filter((calendarEvent) => {
          const startDayNumber = calendarEvent.daysRange.start;
          const endDayNumber = calendarEvent.daysRange.end || startDayNumber;

          return emptyDay.dayNumber >= startDayNumber && emptyDay.dayNumber <= endDayNumber;
        });

        return {
          ...emptyDay,
          slots: eventsPerDay
            .map((calendarEvent) => ({
              type: 'event',
              position: getPosition(emptyDay.dayNumber, calendarEvent),
              data: calendarEvent,
            }))
            .sort((a, b) => a.position - b.position) as any,
        };
      });
    });

    return res.flat().map((day) => ({ ...day, slots: helpers.addEmptySlots(day.slots) }));
  }

  private splitByPeriods(days: types.TCalendarDay[]) {
    return splitEvery(DaysGenerator.PERIOD_SIZE, days);
  }

  private generateEmptyDays(daysRange: { start: number; end: number }) {
    return times<types.TCalendarDay>(
      (index) => ({
        dayNumber: index + daysRange.start,
        date: null,
      }),
      daysRange.end + 1 - daysRange.start,
    );
  }
}
